import { styled } from 'opensolar-ui'
import type { CSSProperties, ElementType, ReactElement } from 'react'
import { forwardRef } from 'react'
import type { IconSize, MaterialSymbolWeight, PolymorphicComponentProps, SymbolCodepoints } from './types'
import { useIsFontLoaded } from './useIsFontLoaded'
import { getFontVariationSettings, getOpticalSizeBasedOnIconSize } from './utils'
export type { IconSize, MaterialSymbolWeight, SymbolCodepoints } from './types'

export type MaterialSymbolProps = {
  /** Required. The name of the icon to render. */
  icon: SymbolCodepoints
  /** Default `false`.
   *
   * Fill gives you the ability to modify the default icon style. A single icon can render both unfilled and filled states. */
  // fill?: boolean
  /** Weight defines the symbol’s stroke weight, with a range of weights between thin (100) and heavy (700). Weight can also affect the overall size of the symbol. */
  weight?: MaterialSymbolWeight
  /** Weight and grade affect a symbol’s thickness. Adjustments to grade are more granular than adjustments to weight and have a small impact on the size of the symbol. */
  // grade?: number
  /** Size defines different icon sizes used in OS design */
  size?: IconSize
  /** Default `'inherit'`.
   *
   * Optical Size defines the icon width and height in pixels. For the image to look the same at different sizes, the stroke weight (thickness) changes as the icon optical size scales. */
  // opticalSize?: number
  /** Default `'inherit'`
   *
   * Color accepts key values (`'red'`, `'blue'`, `'indigo'`, etc.), `<hex-color>`, `<rgb()>`, `<hsl()>` and `<hwb()>` values.  */
  color?: CSSProperties['color']
  style?: CSSProperties
  className?: string
}

export type PolymorphicMaterialSymbolProps<C extends ElementType> = PolymorphicComponentProps<C, MaterialSymbolProps>

export const MaterialSymbol = forwardRef<HTMLSpanElement, PolymorphicMaterialSymbolProps<'span'>>(
  (
    {
      icon,
      onClick,
      as,
      weight = 300,
      // grade,
      size,
      // opticalSize,
      style: propStyle,
      color,
      className = '',
      ...props
    },
    ref
  ): ReactElement => {
    const isFontLoaded = useIsFontLoaded('Material Symbols Rounded Variable')

    const oSize = getOpticalSizeBasedOnIconSize(size)

    // If opticalSize is not provided, it is derived from the icon size. The default weight for every icon size is 300.
    const fontStyles = getFontVariationSettings(false, weight, undefined, oSize)
    const style = {
      ...fontStyles,
      ...propStyle,
      color,
      opacity: isFontLoaded ? 1 : 0,
    }

    const StyledSymbole = styled('span')({
      fontFamily: 'Material Symbols Rounded Variable',
      userSelect: 'none',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontSize: 'inherit',
      display: 'inline-block',
      lineHeight: 1,
      textTransform: 'none',
      letterSpacing: 'normal',
      wordWrap: 'normal',
      whiteSpace: 'nowrap',
      direction: 'ltr',
      transition: 'opacity 0.1s ease-in',
    })

    return (
      <StyledSymbole {...props} className={`OSUI-material-symbols ${className}`} ref={ref} style={style}>
        {icon}
      </StyledSymbole>
    )
  }
) as <C extends ElementType>(props: PolymorphicMaterialSymbolProps<C>) => ReactElement
